import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { View, TouchableOpacity, Platform, ActivityIndicator, ScrollView, Animated, Easing, PanResponder } from "react-native";
import Svg, { G, Line, Circle, Path, Text as SvgText } from "react-native-svg";
import Text from "app/components/Text";
import Icon from "app/components/Icon";
import apiActions from "app/utils/apiActions";
import valueActions from "app/utils/valueActions";
import prefActions from "app/utils/prefActions";
import valueHelpers from "app/utils/valueHelpers";
import storageActions from "app/utils/storageActions";
import store from "app/redux/store";
import MomentBullet from "app/components/chapter/MomentBullet";
import { StyleContext } from "app/contexts/Style";

// Maps of the psyche, ported from the albedo prototype. Four views of one
// dataset (redux value index + values/map_stats):
//   circles       — zoomable pack: what each life-area is made of
//   constellation — values as stars, clustered by graph structure
//   river         — streamgraph of moments per life-area per month
//   garden        — recursive branches, thickness = life lived in each
const HIDDEN = (v) => v.archived_at || v.is_note;
const IS_WEB = Platform.OS === "web";
const jitter = (id, salt) => {
  const x = Math.sin(id * 127.1 + salt * 311.7) * 43758.5453;
  return x - Math.floor(x);
};

// match the app's system font inside SVG (web SVG defaults to serif)
const FONT =
  Platform.OS === "web"
    ? "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
    : undefined;

// if a visualization throws, show the message instead of killing the route
class MapErrorBoundary extends React.Component {
  state = { err: null };
  static getDerivedStateFromError(err) {
    return { err };
  }
  render() {
    if (this.state.err) {
      return (
        <Text style={{ margin: 24, fontSize: 12, color: "#c0504d" }}>
          map crashed: {String((this.state.err && this.state.err.message) || this.state.err)}
        </Text>
      );
    }
    return this.props.children;
  }
}

// while the index loads: one circle, in the map's own language, with a
// short arc orbiting its edge as the loading indicator
function LoadingCircle({ colors }) {
  const spin = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.timing(spin, {
        toValue: 1,
        duration: 1400,
        easing: Easing.linear,
        useNativeDriver: Platform.OS !== "web",
      })
    );
    loop.start();
    return () => loop.stop();
  }, []);
  const rotate = spin.interpolate({ inputRange: [0, 1], outputRange: ["0deg", "360deg"] });
  const R = 64;
  const D = R * 2 + 8;
  const circ = Math.PI * 2 * R;
  return (
    <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
      <View style={{ width: D, height: D, alignItems: "center", justifyContent: "center" }}>
        <Svg width={D} height={D} style={{ position: "absolute" }}>
          <Circle
            cx={D / 2}
            cy={D / 2}
            r={R}
            stroke={colors.trim}
            strokeWidth={1.2}
            fill={colors.fill}
            fillOpacity={0.3}
          />
        </Svg>
        <Animated.View
          style={{ position: "absolute", width: D, height: D, transform: [{ rotate }] }}
        >
          <Svg width={D} height={D}>
            <Circle
              cx={D / 2}
              cy={D / 2}
              r={R}
              stroke={colors.active}
              strokeWidth={2}
              strokeLinecap="round"
              fill="none"
              strokeDasharray={`${circ * 0.22}, ${circ * 0.78}`}
            />
          </Svg>
        </Animated.View>
      </View>
    </View>
  );
}

export default function Atlas({ navigation }) {
  const { colors, colorScheme } = useContext(StyleContext);
  const valueColor = (v) => valueHelpers.getColor(v, colorScheme, colors.inactive);
  const [mode, setMode] = useState("pack");
  const [privacy, setPrivacy] = useState(false);
  const [yearCut, setYearCut] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsErr, setStatsErr] = useState(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [showFilter, setShowFilter] = useState(false);
  const [excludedCats, setExcludedCats] = useState(() => new Set());
  const [rawValues, setRawValues] = useState(() => store.getState().values || []);
  // what seeds the top level: active = activity-weighted roots,
  // all = every root, focus = preference focus values
  const [rootSource, setRootSource] = useState("active");
  const [indexLoading, setIndexLoading] = useState(false);
  // a persisted timeframe wins over the current-year default
  const yearCutPersistedRef = useRef(false);

  useEffect(() => {
    // always refresh — the store hydrates from a localStorage cache at boot,
    // and a stale cache (old shape, missing fields) blanks the whole map
    setIndexLoading(true);
    valueActions
      .indexLite()
      .catch(() => {})
      .finally(() => setIndexLoading(false));
    // focus_ids feed the "focus" root source and the compass — Atlas can't
    // assume another screen already fetched preferences
    prefActions.get().catch(() => {});
    const unsub = store.subscribe(() => setRawValues(store.getState().values || []));
    storageActions
      .fetchItem("mapExcludedCats", [])
      .then((arr) => {
        if (Array.isArray(arr) && arr.length) setExcludedCats(new Set(arr));
      })
      .catch(() => {});
    storageActions
      .fetchItem("atlasRootSource", "active")
      .then((m) => {
        if (["active", "all", "focus"].includes(m)) setRootSource(m);
      })
      .catch(() => {});
    storageActions
      .fetchItem("atlasYearCut", null)
      .then((v) => {
        if (v === "all") {
          yearCutPersistedRef.current = true;
          setYearCut(null);
        } else if (typeof v === "string" && v) {
          yearCutPersistedRef.current = true;
          setYearCut(v);
        }
      })
      .catch(() => {});
    (async () => {
      try {
        const r = await apiActions.request("values/map_stats", "GET");
        const st = (r && r.stats) || [];
        setStats(st);
        // default to the current year (falls back to "all" if it has no
        // data) — unless a persisted timeframe already took over
        const cy = String(new Date().getFullYear());
        if (!yearCutPersistedRef.current && st.some((s) => s.month.startsWith(cy)))
          setYearCut(cy);
        if (!r?.success) setStatsErr("stats unavailable");
      } catch (e) {
        setStatsErr(String(e.message || e));
      }
    })();
    return unsub;
  }, []);

  // categories available for filtering
  const categories = useMemo(() => {
    const cs = new Set();
    for (const v of rawValues) {
      if (v.category) cs.add(v.category);
    }
    return Array.from(cs).sort();
  }, [rawValues]);

  const allValues = useMemo(
    () =>
      excludedCats.size
        ? rawValues.filter((v) => !v.category || !excludedCats.has(v.category))
        : rawValues,
    [rawValues, excludedCats]
  );

  /* ── derived structure ── */
  const derived = useMemo(() => {
    const byId = new Map();
    for (const v of allValues) byId.set(v.id, v);
    const childrenOf = new Map();
    for (const v of allValues) {
      for (const pid of v.parent_ids || []) {
        if (!childrenOf.has(pid)) childrenOf.set(pid, []);
        childrenOf.get(pid).push(v);
      }
    }
    // per-value monthly counts (optionally cut off at a year)
    const own = new Map();
    const lastMonth = new Map();
    const months = new Set();
    for (const s of stats || []) {
      if (yearCut && s.month > `${yearCut}-12`) continue;
      months.add(s.month);
      own.set(s.value_id, (own.get(s.value_id) || 0) + s.count);
      if (!lastMonth.has(s.value_id) || s.month > lastMonth.get(s.value_id))
        lastMonth.set(s.value_id, s.month);
    }
    // subtree weight with cycle guard
    const subtree = new Map();
    const walk = (id, lineage) => {
      if (subtree.has(id)) return subtree.get(id);
      if (lineage.has(id)) return 0;
      lineage.add(id);
      let w = own.get(id) || 0;
      for (const k of childrenOf.get(id) || []) w += walk(k.id, lineage);
      lineage.delete(id);
      subtree.set(id, w);
      return w;
    };
    // root ancestor per value
    const rootOf = new Map();
    const findRoot = (id, seen) => {
      if (rootOf.has(id)) return rootOf.get(id);
      if (seen.has(id)) return id;
      seen.add(id);
      const v = byId.get(id);
      const pid = v && (v.parent_ids || [])[0];
      const r = pid && byId.has(pid) ? findRoot(pid, seen) : id;
      rootOf.set(id, r);
      return r;
    };
    // roots: no parents, or every parent was filtered out of the map
    const roots = allValues.filter(
      (v) => !HIDDEN(v) && !(v.parent_ids || []).some((pid) => byId.has(pid))
    );
    for (const r of roots) walk(r.id, new Set());
    const sortedMonths = Array.from(months).sort();
    return { byId, childrenOf, own, lastMonth, subtree, rootOf, findRoot, roots, sortedMonths };
  }, [allValues, stats, yearCut]);

  const years = useMemo(() => {
    const ys = new Set((stats || []).map((s) => s.month.slice(0, 4)));
    return Array.from(ys).sort();
  }, [stats]);

  // ▶ replay: step through the years, ending on "all"
  useEffect(() => {
    if (!playing || !years.length) return;
    let i = 0;
    setYearCut(years[0]);
    const t = setInterval(() => {
      i += 1;
      if (i < years.length) setYearCut(years[i]);
      else {
        setYearCut(null);
        setPlaying(false);
      }
    }, 1400);
    return () => clearInterval(t);
  }, [playing]);

  const topRoots = useMemo(() => {
    if (rootSource === "focus") {
      const focusIds = store.getState().preferences?.focusIds || [];
      const focus = focusIds
        .map((id) => derived.byId.get(id))
        .filter((v) => v && !HIDDEN(v));
      if (focus.length) return focus;
      // fall through to roots when no focus is set
    }
    const weighted = derived.roots
      .map((r) => ({ v: r, w: derived.subtree.get(r.id) || 0 }))
      .sort((a, b) => b.w - a.w);
    const active = weighted.filter((r) => r.w > 0);
    // never blank the canvas: if stats don't overlap the index (stale cache,
    // odd year cut), show the structure unweighted instead of nothing
    const chosen =
      rootSource === "all" || !stats || !active.length ? weighted : active;
    return chosen.map((r) => r.v);
  }, [derived, stats, rootSource]);

  const pickRootSource = (m) => {
    setRootSource(m);
    storageActions.saveItem("atlasRootSource", m);
  };

  // only explicit chip presses persist — replay's automatic year stepping
  // must not overwrite the remembered timeframe
  const pickYearCut = (y) => {
    yearCutPersistedRef.current = true;
    setYearCut(y);
    storageActions.saveItem("atlasYearCut", y == null ? "all" : y);
  };

  const recentGlow = (id) => {
    const lm = derived.lastMonth.get(id);
    if (!lm) return false;
    const cutRef = yearCut ? `${yearCut}-12` : derived.sortedMonths[derived.sortedMonths.length - 1] || "";
    return lm >= cutRef.slice(0, 5) + String(Math.max(1, parseInt(cutRef.slice(5, 7), 10) - 2)).padStart(2, "0");
  };

  const Chip = ({ on, label, onPress }) => (
    <TouchableOpacity
      onPress={onPress}
      style={{
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 10,
        backgroundColor: on ? colors.active : colors.fill,
      }}
    >
      <Text style={{ fontSize: 12.5, color: on ? colors.empty : colors.foreground }}>{label}</Text>
    </TouchableOpacity>
  );

  const W = size.w;
  const H = Math.max(size.h, 300);
  const openValue = (id) => navigation.navigate("Value", { id });

  return (
    <View style={{ flex: 1, backgroundColor: colors.empty }}>
      <View style={{ flexDirection: "row", gap: 8, paddingHorizontal: 20, paddingVertical: 8, flexWrap: "wrap", alignItems: "center" }}>
        <Chip on={mode === "pack"} label="circles" onPress={() => setMode("pack")} />
        <Chip on={mode === "constellation"} label="constellation" onPress={() => setMode("constellation")} />
        <Chip on={mode === "river"} label="river of years" onPress={() => setMode("river")} />
        <Chip on={mode === "garden"} label="garden" onPress={() => setMode("garden")} />
        <Chip on={mode === "compass"} label="compass" onPress={() => setMode("compass")} />
        {categories.length ? (
          <Chip
            on={showFilter || excludedCats.size > 0}
            label={excludedCats.size ? `filter (${excludedCats.size})` : "filter"}
            onPress={() => setShowFilter((f) => !f)}
          />
        ) : null}
        <Chip on={privacy} label={privacy ? "◈" : "◇"} onPress={() => setPrivacy((p) => !p)} />
        <View style={{ flexDirection: "row", gap: 6, marginLeft: 12, flexWrap: "wrap" }}>
          <Chip on={rootSource === "active"} label="active" onPress={() => pickRootSource("active")} />
          <Chip on={rootSource === "all"} label="all roots" onPress={() => pickRootSource("all")} />
          <Chip on={rootSource === "focus"} label="focus" onPress={() => pickRootSource("focus")} />
        </View>
        {years.length > 1 ? (
          <View style={{ flexDirection: "row", gap: 6, marginLeft: 12, flexWrap: "wrap" }}>
            <Chip on={playing} label="▶ replay" onPress={() => setPlaying((p) => !p)} />
            <Chip on={!yearCut} label="all" onPress={() => pickYearCut(null)} />
            {years.map((y) => (
              <Chip key={y} on={yearCut === y} label={`’${y.slice(2)}`} onPress={() => pickYearCut(y)} />
            ))}
          </View>
        ) : null}
      </View>

      {showFilter ? (
        <View style={{ flexDirection: "row", gap: 6, paddingHorizontal: 20, paddingBottom: 6, flexWrap: "wrap", alignItems: "center" }}>
          <Text style={{ fontSize: 11.5, color: colors.inactive }}>showing:</Text>
          {categories.map((c) => (
            <Chip
              key={c}
              on={!excludedCats.has(c)}
              label={c}
              onPress={() => {
                const next = new Set(excludedCats);
                if (next.has(c)) next.delete(c);
                else next.add(c);
                setExcludedCats(next);
                storageActions.saveItem("mapExcludedCats", [...next]);
              }}
            />
          ))}
        </View>
      ) : null}

      {statsErr ? (
        <Text style={{ paddingHorizontal: 20, fontSize: 12, color: colors.inactive, fontStyle: "italic" }}>
          activity stats unavailable ({statsErr}) — showing structure only
        </Text>
      ) : null}

      {!stats && !statsErr && allValues.length ? (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6, paddingHorizontal: 20, paddingBottom: 4 }}>
          <ActivityIndicator size="small" color={colors.inactive} />
          <Text style={{ fontSize: 11.5, color: colors.inactive, fontStyle: "italic" }}>
            loading activity stats…
          </Text>
        </View>
      ) : null}

      <View style={{ flex: 1, width: "100%" }} onLayout={(e) => setSize({ w: e.nativeEvent.layout.width, h: e.nativeEvent.layout.height })}>
        {indexLoading || !allValues.length ? (
          // the loader replaces the map entirely while the index fetch runs —
          // cached values would otherwise draw a partial, statless map
          <LoadingCircle colors={colors} />
        ) : W > 0 ? (
          <View style={{ flex: 1 }}>
            <MapErrorBoundary key={mode}>
            {mode === "pack" ? (
              <Pack {...{ derived, topRoots, W, H, colors, privacy, openValue, valueColor }} />
            ) : mode === "constellation" ? (
              <Constellation {...{ derived, topRoots, W, H, colors, privacy, recentGlow, openValue, valueColor }} />
            ) : mode === "river" ? (
              <River {...{ derived, W, H, colors, privacy, stats, yearCut, valueColor }} />
            ) : mode === "compass" ? (
              <Compass
                {...{ derived, stats, W, H, colors, privacy, valueColor, openValue }}
                focusIds={store.getState().preferences?.focusIds || []}
              />
            ) : (
              <Garden {...{ derived, topRoots, W, H, colors, privacy, recentGlow, valueColor }} />
            )}
            </MapErrorBoundary>
          </View>
        ) : null}
      </View>
    </View>
  );
}

/* ═══ 0. ZOOMABLE PACK ═══ */
// Life-areas as circles containing the circles of their parts, recursively.
// Click a circle to glide into it; click the background to zoom out one level.
function packCircles(circles) {
  if (!circles.length) return;
  const placed = [];
  const sorted = circles.slice().sort((a, b) => b.r - a.r);
  sorted.forEach((c, idx) => {
    if (idx === 0) {
      c.cx = 0;
      c.cy = 0;
      placed.push(c);
      return;
    }
    if (idx === 1) {
      c.cx = placed[0].r + c.r + 2;
      c.cy = 0;
      placed.push(c);
      return;
    }
    let best = null;
    for (const p of placed) {
      for (let a = 0; a < Math.PI * 2; a += 0.28) {
        const x = p.cx + (p.r + c.r + 2) * Math.cos(a);
        const y = p.cy + (p.r + c.r + 2) * Math.sin(a);
        let ok = true;
        for (const q of placed) {
          if (Math.hypot(x - q.cx, y - q.cy) < q.r + c.r + 1.5) {
            ok = false;
            break;
          }
        }
        if (ok) {
          const d = Math.hypot(x, y);
          if (!best || d < best.d) best = { x, y, d };
        }
      }
    }
    c.cx = best ? best.x : placed.length * (c.r * 2 + 4);
    c.cy = best ? best.y : 0;
    placed.push(c);
  });
  const minX = Math.min(...circles.map((c) => c.cx - c.r));
  const maxX = Math.max(...circles.map((c) => c.cx + c.r));
  const minY = Math.min(...circles.map((c) => c.cy - c.r));
  const maxY = Math.max(...circles.map((c) => c.cy + c.r));
  const sx = (minX + maxX) / 2;
  const sy = (minY + maxY) / 2;
  for (const c of circles) {
    c.cx -= sx;
    c.cy -= sy;
  }
}

function Pack({ derived, topRoots, W, H, colors, privacy, openValue, valueColor }) {
  // diving re-roots the pack at a value, giving its subtree a fresh depth
  // budget — without this, circles past the precomputed depth caps are
  // permanently empty no matter how far you zoom
  const [rootId, setRootId] = useState(null);
  const rootIdRef = useRef(null);
  // venn-style parents view: the value centered inside ALL its parents
  const [parentsOfId, setParentsOfId] = useState(null);
  const parentsOfIdRef = useRef(null);
  // circles zoomed close enough materialize their withheld children in
  // place (with hysteresis: zooming away collapses them again)
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const expandCheckRef = useRef(null);
  const trailRef = useRef([]); // stack of {root, pview}
  const diveRef = useRef(null);
  // pointer handlers capture the first zoomToNode — read derived via a ref
  const derivedRef = useRef(derived);
  derivedRef.current = derived;

  const flat = useMemo(() => {
    const { childrenOf, subtree, own } = derived;
    const K = 3.2;
    // mobile draws fewer nodes — SVG circle counts this high strain a phone
    const KIDS_AT_DEPTH = IS_WEB ? [10, 8, 6, 4] : [8, 6, 4, 3];
    const NODE_BUDGET = IS_WEB ? 1500 : 450;
    // DAG guard: each value is packed once, under the first (heaviest-path)
    // parent that reaches it. Prevents duplicate keys and exponential blowup.
    const claimed = new Set();
    let budget = NODE_BUDGET;

    const makeNode = (v, depth) => {
      claimed.add(v.id);
      budget -= 1;
      // totalKids counts ALL visible children — including ones claimed
      // under another parent or cut by depth — so truncation is knowable
      const allKids = (childrenOf.get(v.id) || []).filter((k) => !HIDDEN(k));
      const totalKids = allKids.length;
      let kidVals = [];
      if (depth < KIDS_AT_DEPTH.length && budget > 0) {
        // a dived/parents view has ONE root — it shows ALL its children,
        // the caps only bound the levels below it
        const cap =
          depth === 0 && (rootId != null || parentsOfId != null)
            ? 9999
            : KIDS_AT_DEPTH[depth];
        const eligible = allKids.filter((k) => !claimed.has(k.id));
        kidVals = eligible
          .map((k) => ({ k, w: subtree.get(k.id) || 0 }))
          .filter((e) => depth < 2 || e.w > 0)
          .sort((a, b) => b.w - a.w)
          .slice(0, cap)
          .map((e) => e.k);
        kidVals.forEach((k) => claimed.add(k.id));
      }
      const children = kidVals.map((k) => makeNode(k, depth + 1));
      const ownW = own.get(v.id) || 0;
      let r;
      if (!children.length) {
        // a hub whose family was withheld (children claimed under other
        // parents, or depth caps) still deserves its true size — otherwise
        // multi-parent areas like these read as empty specks
        const w = totalKids ? Math.max(ownW, subtree.get(v.id) || 0) : ownW;
        // ×1.4 calibration: approximate the radius this family would occupy
        // if packed — bare sqrt reads 2-3× smaller than a real container
        r = Math.sqrt(w + 1) * K * (totalKids ? 1.4 : 1) + 4;
      } else {
        packCircles(children);
        const enclose = Math.max(...children.map((c) => Math.hypot(c.cx, c.cy) + c.r));
        r = enclose * 1.06 + Math.sqrt(ownW + 1) * 1.2 + 8;
      }
      return { v, depth, children, totalKids, ownW, r, cx: 0, cy: 0, parent: null };
    };

    // parents view: the value packed normally at the center, its parents
    // drawn as larger circles offset around it so every one CONTAINS it —
    // the overlap region (venn-style) is where the value lives
    if (parentsOfId != null) {
      const cv = derived.byId.get(parentsOfId);
      if (cv) {
        const center = makeNode(cv, 0);
        const parents = (cv.parent_ids || [])
          .map((pid) => derived.byId.get(pid))
          .filter((p) => p && !HIDDEN(p));
        const rv = center.r;
        const maxPW = Math.max(...parents.map((p) => subtree.get(p.id) || 0), 1);
        const out = [];
        parents.forEach((p, i) => {
          const wN = Math.sqrt((subtree.get(p.id) || 0) / maxPW);
          const R = rv * (1.5 + 0.8 * wN);
          const a = -Math.PI / 2 + (2 * Math.PI * i) / Math.max(parents.length, 1);
          const d = (R - rv) * 0.72; // < R - rv → the parent contains the value
          out.push({
            v: p,
            depth: 0,
            children: [],
            totalKids: (childrenOf.get(p.id) || []).filter((k) => !HIDDEN(k)).length,
            ownW: own.get(p.id) || 0,
            r: R,
            cx: 0,
            cy: 0,
            x: Math.cos(a) * d,
            y: Math.sin(a) * d,
            parent: null,
            isParentRing: true,
          });
        });
        const walkAbsP = (n, px, py, parent) => {
          n.x = px + n.cx;
          n.y = py + n.cy;
          n.parent = parent;
          out.push(n);
          for (const c of n.children) walkAbsP(c, n.x, n.y, n);
        };
        center.cx = 0;
        center.cy = 0;
        walkAbsP(center, 0, 0, null);
        return out;
      }
    }
    let baseRoots = topRoots;
    if (rootId != null) {
      const rv = derived.byId.get(rootId);
      if (rv) baseRoots = [rv];
    }
    const roots = baseRoots.map((r) => makeNode(r, 0));
    packCircles(roots);
    const out = [];
    const walkAbs = (n, px, py, parent) => {
      n.x = px + n.cx;
      n.y = py + n.cy;
      n.parent = parent;
      out.push(n);
      for (const c of n.children) walkAbs(c, n.x, n.y, n);
    };
    for (const r of roots) walkAbs(r, 0, 0, null);

    // progressive expansion: a childless-but-not-atomic node the user has
    // zoomed into gets its children packed INSIDE its existing circle —
    // the rest of the layout is untouched, so this is cheap and stable.
    // These children may duplicate nodes claimed elsewhere → unique keys.
    const expandNode = (n) => {
      if (n.children.length || !n.totalKids || !expandedIds.has(n.v.id)) return;
      const kids = (childrenOf.get(n.v.id) || [])
        .filter((k) => !HIDDEN(k))
        .map((k) => ({ k, w: subtree.get(k.id) || 0 }))
        .sort((a, b) => b.w - a.w)
        .slice(0, KIDS_AT_DEPTH[0])
        .map((e) => e.k);
      if (!kids.length) return;
      const children = kids.map((k) => {
        const kidsOfK = (childrenOf.get(k.id) || []).filter((x) => !HIDDEN(x));
        const w = kidsOfK.length
          ? Math.max(own.get(k.id) || 0, subtree.get(k.id) || 0)
          : own.get(k.id) || 0;
        return {
          v: k,
          depth: n.depth + 1,
          children: [],
          totalKids: kidsOfK.length,
          ownW: own.get(k.id) || 0,
          r: Math.sqrt(w + 1) * K * (kidsOfK.length ? 1.4 : 1) + 4,
          cx: 0,
          cy: 0,
          parent: n,
          key: `${n.key || n.v.id}-${k.id}`,
        };
      });
      packCircles(children);
      const enclose = Math.max(...children.map((c) => Math.hypot(c.cx, c.cy) + c.r));
      const fit = (n.r * 0.86) / Math.max(enclose, 1);
      for (const c of children) {
        c.r *= fit;
        c.cx *= fit;
        c.cy *= fit;
        c.x = n.x + c.cx;
        c.y = n.y + c.cy;
        out.push(c);
      }
      n.children = children;
    };
    let pass = out.slice();
    while (pass.length) {
      const nextPass = [];
      for (const n of pass) {
        const had = n.children.length;
        expandNode(n);
        if (!had && n.children.length) nextPass.push(...n.children);
      }
      pass = nextPass;
    }
    return out;
  }, [derived, topRoots, rootId, parentsOfId, expandedIds]);

  const [view, setView] = useState({ tx: 0, ty: 0, s: 1 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const flatRef = useRef(flat);
  flatRef.current = flat;
  const [hoverId, setHoverId] = useState(null);
  const [focus, setFocus] = useState(null);
  const [showLegend, setShowLegend] = useState(false);
  const focusRef = useRef(null);
  focusRef.current = focus;
  const containerRef = useRef(null);
  const animRef = useRef(null);
  // once the user moves the camera (pinch/pan/wheel), layout rebuilds —
  // auto-expansion, data refreshes — must never reposition it under them
  const userMovedRef = useRef(false);

  const fitAll = () => {
    const roots = flatRef.current.filter((n) => !n.parent);
    if (!roots.length || !W) return null;
    const minX = Math.min(...roots.map((n) => n.x - n.r));
    const maxX = Math.max(...roots.map((n) => n.x + n.r));
    const minY = Math.min(...roots.map((n) => n.y - n.r));
    const maxY = Math.max(...roots.map((n) => n.y + n.r));
    const s = Math.min(W / (maxX - minX + 20), H / (maxY - minY + 20)) * 0.94;
    return { s, tx: W / 2 - ((minX + maxX) / 2) * s, ty: H / 2 - ((minY + maxY) / 2) * s };
  };

  const animateTo = (target) => {
    if (!target) return;
    if (animRef.current) cancelAnimationFrame(animRef.current);
    const from = { ...viewRef.current };
    const t0 = Date.now();
    const step = () => {
      const t = Math.min((Date.now() - t0) / 480, 1);
      const e = 1 - Math.pow(1 - t, 3);
      setView({
        s: from.s + (target.s - from.s) * e,
        tx: from.tx + (target.tx - from.tx) * e,
        ty: from.ty + (target.ty - from.ty) * e,
      });
      if (t < 1) animRef.current = requestAnimationFrame(step);
    };
    animRef.current = requestAnimationFrame(step);
  };

  const zoomToNode = (n) => {
    // a circle with real children that the precomputed tree didn't include
    // (depth caps, activity filter, claimed-elsewhere) re-roots the pack
    // there so the children actually load in
    const realKids = (derivedRef.current.childrenOf.get(n.v.id) || []).filter(
      (k) => !HIDDEN(k)
    ).length;
    // dive whenever the drawn children UNDERSELL the real ones — a node
    // showing 4 of 80 kids re-roots to reveal all, not just an empty one
    if (realKids > n.children.length && n.v.id !== rootIdRef.current) {
      diveRef.current = n.v.id;
      trailRef.current.push({
        root: rootIdRef.current,
        pview: parentsOfIdRef.current,
      });
      rootIdRef.current = n.v.id;
      parentsOfIdRef.current = null;
      setParentsOfId(null);
      setRootId(n.v.id);
      return;
    }
    const s2 = Math.min(Math.max((Math.min(W, H) * 0.44) / n.r, 0.15), 60);
    animateTo({ s: s2, tx: W / 2 - n.x * s2, ty: H / 2 - n.y * s2 });
    setFocus(n);
  };

  const enterParentsView = (valueId) => {
    trailRef.current.push({
      root: rootIdRef.current,
      pview: parentsOfIdRef.current,
    });
    parentsOfIdRef.current = valueId;
    diveRef.current = valueId;
    setParentsOfId(valueId);
  };

  const upOneLevel = () => {
    const f = focusRef.current;
    if (f && f.parent) zoomToNode(f.parent);
    else if (trailRef.current.length) {
      // climb back out of a dive / parents view
      const prev = trailRef.current.pop();
      rootIdRef.current = prev.root;
      parentsOfIdRef.current = prev.pview;
      diveRef.current = prev.pview != null ? prev.pview : prev.root;
      setParentsOfId(prev.pview);
      setRootId(prev.root);
    } else {
      setFocus(null);
      animateTo(fitAll());
    }
  };

  // fit on rebuild; after a dive, land focused on the dived-into value —
  // and when a data refresh rebuilt the layout (e.g. ⌘↑ fetching parents
  // into the store), keep the user's place instead of resetting to the top
  useEffect(() => {
    const divedRoot =
      diveRef.current != null
        ? flat.find((n) => !n.parent && n.v.id === diveRef.current)
        : null;
    if (divedRoot) {
      const f = fitAll();
      if (f) setView(f);
      setFocus(divedRoot);
      userMovedRef.current = false; // fresh view — camera is programmatic again
    } else if (focusRef.current) {
      const keep = flat.find((n) => n.v.id === focusRef.current.v.id);
      if (keep) {
        setFocus(keep);
        if (!userMovedRef.current) {
          const s2 = Math.min(Math.max((Math.min(W, H) * 0.44) / keep.r, 0.15), 60);
          animateTo({ s: s2, tx: W / 2 - keep.x * s2, ty: H / 2 - keep.y * s2 });
        }
      } else {
        const f = fitAll();
        if (f) setView(f);
        setFocus(null);
      }
    } else if (userMovedRef.current) {
      // the user owns the camera — an auto-expansion or data-refresh
      // rebuild must not snap the view back to the overview
    } else {
      const f = fitAll();
      if (f) setView(f);
      setFocus(null);
    }
    diveRef.current = null;
  }, [flat, W, H]);

  // watch the camera: a circle filling ~a third of the screen auto-loads
  // its withheld children; zoomed-away expansions collapse again so the
  // node count stays bounded. Debounced off the hot zoom path.
  useEffect(() => {
    if (expandCheckRef.current) clearTimeout(expandCheckRef.current);
    expandCheckRef.current = setTimeout(() => {
      const v = viewRef.current;
      const TH = Math.min(W, H) * 0.3;
      setExpandedIds((prev) => {
        let changed = false;
        const next = new Set(prev);
        for (const n of flatRef.current) {
          const sr = n.r * v.s;
          const sx = n.x * v.s + v.tx;
          const sy = n.y * v.s + v.ty;
          const onScreen = sx + sr > 0 && sx - sr < W && sy + sr > 0 && sy - sr < H;
          if (!next.has(n.v.id)) {
            if (!n.children.length && n.totalKids > 0 && onScreen && sr > TH) {
              next.add(n.v.id);
              changed = true;
            }
          } else if (sr < TH * 0.35 || !onScreen) {
            next.delete(n.v.id);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 140);
  }, [view, W, H]);

  useEffect(() => {
    if (Platform.OS !== "web") return;
    const el = containerRef.current;
    if (!el || !el.addEventListener) return;
    let drag = null;
    const pt = (e) => {
      const r = el.getBoundingClientRect();
      return { px: e.clientX - r.left, py: e.clientY - r.top };
    };
    const containing = (px, py) => {
      const v = viewRef.current;
      const x = (px - v.tx) / v.s;
      const y = (py - v.ty) / v.s;
      let deepest = null;
      for (const n of flatRef.current) {
        if (Math.hypot(n.x - x, n.y - y) <= n.r) {
          if (!deepest || n.r < deepest.r) deepest = n;
        }
      }
      return deepest;
    };
    const onWheel = (e) => {
      e.preventDefault();
      if (animRef.current) cancelAnimationFrame(animRef.current);
      const { px, py } = pt(e);
      const v = viewRef.current;
      // zoom-out floor follows the fit-everything scale, not a constant —
      // subtree sizing can make the layout larger than 0.05 accommodates
      const fit = fitAll();
      const minS = Math.min(0.05, (fit ? fit.s : 0.05) * 0.7);
      const s2 = Math.min(Math.max(v.s * Math.exp(-e.deltaY * 0.0012), minS), 60);
      const wx = (px - v.tx) / v.s;
      const wy = (py - v.ty) / v.s;
      userMovedRef.current = true;
      setView({ s: s2, tx: px - wx * s2, ty: py - wy * s2 });
    };
    const onDown = (e) => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      const { px, py } = pt(e);
      drag = { px, py, tx: viewRef.current.tx, ty: viewRef.current.ty, moved: false };
    };
    const onMove = (e) => {
      const { px, py } = pt(e);
      if (drag) {
        const dx = px - drag.px;
        const dy = py - drag.py;
        if (Math.abs(dx) + Math.abs(dy) > 3) drag.moved = true;
        if (drag.moved) {
          // capture now — the updater runs later, when drag may already be null
          const ntx = drag.tx + dx;
          const nty = drag.ty + dy;
          userMovedRef.current = true;
          setView((v) => ({ ...v, tx: ntx, ty: nty }));
        }
      } else {
        const n = containing(px, py);
        setHoverId(n ? n.v.id : null);
      }
    };
    const onUp = (e) => {
      if (drag && !drag.moved) {
        const { px, py } = pt(e);
        // focus exactly what the hover highlights: the deepest circle
        // under the pointer, not one level in at a time
        const n = containing(px, py);
        if (n) zoomToNode(n);
        else upOneLevel();
      }
      drag = null;
    };
    const onLeave = () => {
      setHoverId(null);
      drag = null;
    };
    const onKey = (e) => {
      const t = document.activeElement && document.activeElement.tagName;
      if (t === "INPUT" || t === "TEXTAREA") return;
      if (e.key === "Escape") upOneLevel();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointerleave", onLeave);
    document.addEventListener("keydown", onKey);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointerleave", onLeave);
      document.removeEventListener("keydown", onKey);
    };
  }, [W, H]);

  // native touch: one finger pans, two fingers pinch-zoom around the
  // midpoint, a still tap hit-tests like the web click (deepest circle
  // wins; background climbs out)
  const gestureRef = useRef(null);
  const panResponder = useMemo(() => {
    if (IS_WEB) return { panHandlers: {} };
    const dist = (t) =>
      Math.hypot(t[0].pageX - t[1].pageX, t[0].pageY - t[1].pageY);
    return PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: (evt) => {
        const t = evt.nativeEvent.touches;
        gestureRef.current = {
          view: { ...viewRef.current },
          moved: false,
          start: t.length ? { x: t[0].locationX, y: t[0].locationY } : null,
          startDist: t.length >= 2 ? dist(t) : null,
        };
      },
      onPanResponderMove: (evt, g) => {
        const st = gestureRef.current;
        if (!st) return;
        const t = evt.nativeEvent.touches;
        if (t.length >= 2) {
          const d = dist(t);
          if (!st.startDist) {
            // second finger landed mid-gesture — rebase
            st.startDist = d;
            st.view = { ...viewRef.current };
            return;
          }
          const fit = fitAll();
          const minS = Math.min(0.05, (fit ? fit.s : 0.05) * 0.7);
          const s2 = Math.min(Math.max(st.view.s * (d / st.startDist), minS), 60);
          const mx = (t[0].locationX + t[1].locationX) / 2;
          const my = (t[0].locationY + t[1].locationY) / 2;
          const wx = (mx - st.view.tx) / st.view.s;
          const wy = (my - st.view.ty) / st.view.s;
          st.moved = true;
          userMovedRef.current = true;
          setView({ s: s2, tx: mx - wx * s2, ty: my - wy * s2 });
        } else if (st.moved || Math.abs(g.dx) + Math.abs(g.dy) > 6) {
          st.moved = true;
          userMovedRef.current = true;
          setView({ ...st.view, s: viewRef.current.s, tx: st.view.tx + g.dx, ty: st.view.ty + g.dy });
        }
      },
      onPanResponderRelease: () => {
        const st = gestureRef.current;
        gestureRef.current = null;
        if (st && !st.moved && st.start) {
          const v = viewRef.current;
          const x = (st.start.x - v.tx) / v.s;
          const y = (st.start.y - v.ty) / v.s;
          let deepest = null;
          for (const n of flatRef.current) {
            if (Math.hypot(n.x - x, n.y - y) <= n.r) {
              if (!deepest || n.r < deepest.r) deepest = n;
            }
          }
          if (deepest) zoomToNode(deepest);
          else upOneLevel();
        }
      },
      onPanResponderTerminate: () => {
        gestureRef.current = null;
      },
    });
  }, [W, H]);

  const s = view.s;

  // normalize containers' own-activity for the fill-depth encoding
  const maxOwn = useMemo(
    () => Math.max(...flat.map((n) => n.ownW || 0), 1),
    [flat]
  );

  // breadcrumb: ancestors of the focused node, root first
  const crumbs = [];
  for (let n = focus; n; n = n.parent) crumbs.unshift(n);

  // exact subtree total for the popup: each distinct descendant counted
  // once — the memoized weights double-count diamond-shaped ancestry
  // (a multi-parent subtree lands in every parent), fine for sizing only
  const focusSubtree = useMemo(() => {
    if (!focus) return 0;
    const { childrenOf, own } = derived;
    const seen = new Set();
    const stack = [focus.v.id];
    let total = 0;
    while (stack.length) {
      const cur = stack.pop();
      if (seen.has(cur)) continue;
      seen.add(cur);
      total += own.get(cur) || 0;
      for (const k of childrenOf.get(cur) || []) stack.push(k.id);
    }
    return total;
  }, [focus, derived]);

  return (
    <View style={{ flex: 1 }}>
      {/* pointer listeners live on this canvas-only wrapper — clicks on the
          popup below must not zoom/dismiss before the link press lands */}
      <View
        ref={containerRef}
        {...(IS_WEB ? {} : panResponder.panHandlers)}
        style={{
          flex: 1,
          cursor: Platform.OS === "web" ? (hoverId ? "pointer" : "grab") : undefined,
        }}
      >
      <Svg width={W} height={H}>
        <G transform={`translate(${view.tx},${view.ty}) scale(${s})`}>
          {flat.map((n) => {
            const c = valueColor(n.v);
            const isLeaf = !n.children.length;
            const hot = hoverId === n.v.id;
            const done = !!n.v.completed_at;
            // containers deepen their fill with the area's OWN direct
            // moments (beyond its parts) — replaces the old center disc
            const ownShare = Math.sqrt(n.ownW || 0) / Math.sqrt(maxOwn);
            return (
              <React.Fragment key={n.key || n.v.id}>
                <Circle
                  cx={n.x}
                  cy={n.y}
                  r={n.r}
                  fill={c}
                  // completed = ghosted: barely-there fill AND a faint dashed
                  // ring — the stroke is what reads at small sizes
                  fillOpacity={
                    done ? (isLeaf ? 0.1 : 0.03) : isLeaf ? 0.5 : 0.05 + 0.3 * ownShare
                  }
                  stroke={hot ? colors.activeHighlight : c}
                  strokeOpacity={hot ? 0.9 : done ? 0.18 : 0.45}
                  strokeWidth={(hot ? 1.6 : 0.9) / s}
                  strokeDasharray={done && !hot ? `${3 / s},${2.5 / s}` : undefined}
                />
                {!n.children.length && n.totalKids > 0 ? (
                  // family withheld here (shown under another parent, or
                  // deeper than the caps) — tap to dive in
                  <Circle
                    cx={n.x}
                    cy={n.y}
                    r={n.r * 0.55}
                    fill="none"
                    stroke={c}
                    strokeOpacity={0.3}
                    strokeWidth={0.7 / s}
                  />
                ) : null}
              </React.Fragment>
            );
          })}
          {!privacy
            ? flat.map((n) => {
                const screenR = n.r * s;
                // labels earn their pixels later on a phone
                const labelMin = IS_WEB ? 11 : 16;
                // venn parents: name sits OUTSIDE the ring, on its far side
                // (their "centers" are the shared overlap zone — useless)
                if (n.isParentRing) {
                  if (screenR < labelMin) return null;
                  const fwp = Math.min((IS_WEB ? 12 : 13) / s, n.r * 0.3);
                  const ang = Math.atan2(n.y, n.x);
                  const lx = n.x + Math.cos(ang) * (n.r + fwp * 1.4);
                  const ly = n.y + Math.sin(ang) * (n.r + fwp * 1.4);
                  return (
                    <SvgText
                      fontFamily={FONT}
                      key={`t${n.key || n.v.id}`}
                      x={lx}
                      y={ly + fwp / 3}
                      fontSize={fwp}
                      fill={hoverId === n.v.id ? colors.activeHighlight : colors.foreground}
                      opacity={0.9}
                      textAnchor="middle"
                    >
                      {(n.v.title || "").slice(0, 24)}
                    </SvgText>
                  );
                }
                if (screenR > Math.min(W, H) * 1.2) return null;
                const fw = Math.min((IS_WEB ? 11 : 12) / s, n.r * 0.45);
                const isLeaf = !n.children.length;
                const hot = hoverId === n.v.id;
                const fade = Math.min(Math.max((screenR - labelMin) / 16, 0), 1) * 0.8;
                return (
                  <SvgText
                    fontFamily={FONT}
                    key={`t${n.key || n.v.id}`}
                    x={n.x}
                    y={isLeaf ? n.y + fw / 3 : n.y - n.r + fw * 1.5}
                    fontSize={fw}
                    fill={hot ? colors.activeHighlight : colors.foreground}
                    opacity={hot ? 1 : fade}
                    textAnchor="middle"
                  >
                    {(n.v.title || "").slice(0, Math.max(14, Math.floor((n.r * 2) / (fw * 0.55))))}
                  </SvgText>
                );
              })
            : null}
        </G>
      </Svg>
      {/* icon overlay — vector-icon glyphs can't render inside react-native-svg,
          so they float above the canvas at each circle's screen position */}
      {flat.map((n) => {
        const screenR = n.r * s;
        if (screenR < (IS_WEB ? 18 : 26) || screenR > Math.min(W, H) * 1.2) return null;
        const iconName = n.v.icon || n.v.inherited_icon;
        if (!iconName) return null;
        const x = n.x * s + view.tx;
        const y = n.y * s + view.ty;
        if (x < -40 || x > W + 40 || y < -40 || y > H + 40) return null;
        const iSize = Math.min(Math.max(screenR * 0.3, 10), 24);
        const isLeaf = !n.children.length;
        const fwS = Math.min(11, n.r * 0.45 * s); // label font in screen px
        // leaves: above the centered title · containers: under the top label
        const iy = isLeaf ? y - fwS * 1.8 - iSize / 2 : (n.y - n.r) * s + view.ty + fwS * 2 + iSize / 2;
        const fade = Math.min(Math.max((screenR - 18) / 16, 0), 1) * 0.85;
        return (
          <View
            key={`i${n.key || n.v.id}`}
            pointerEvents="none"
            style={{ position: "absolute", left: x - iSize / 2, top: iy - iSize / 2, opacity: fade }}
          >
            <Icon name={iconName} color={valueColor(n.v)} size={iSize} />
          </View>
        );
      })}
      </View>

      {/* legend */}
      <TouchableOpacity
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        onPress={() => setShowLegend((l) => !l)}
        style={{ position: "absolute", bottom: 10, left: 12 }}
      >
        <Text style={{ fontSize: 13, color: showLegend ? colors.active : colors.inactive }}>?</Text>
      </TouchableOpacity>
      {showLegend ? (
        <View
          style={{
            position: "absolute",
            bottom: 30,
            left: 10,
            backgroundColor: colors.fill,
            borderColor: colors.trim,
            borderWidth: 1,
            borderRadius: 12,
            padding: 12,
            maxWidth: 280,
          }}
        >
          {[
            {
              swatch: (
                <View style={{ width: 14, height: 14, borderRadius: 7, borderWidth: 1.5, borderColor: colors.active, opacity: 0.6 }} />
              ),
              text: "pale ring — an area made of its parts",
            },
            {
              swatch: (
                <View style={{ width: 11, height: 11, borderRadius: 6, backgroundColor: colors.active, opacity: 0.5, margin: 1.5 }} />
              ),
              text: "solid dot — an atomic value (no parts)",
            },
            {
              swatch: (
                <View style={{ width: 14, height: 14, borderRadius: 7, borderWidth: 1.5, borderColor: colors.active, backgroundColor: colors.active, opacity: 0.45 }} />
              ),
              text: "deeper fill — moments logged on the area itself, beyond its parts",
            },
            {
              swatch: (
                <View style={{ width: 11, height: 11, borderRadius: 6, backgroundColor: colors.active, opacity: 0.12, margin: 1.5 }} />
              ),
              text: "dashed, ghosted ring — completed",
            },
            {
              swatch: (
                <View style={{ width: 14, height: 14, borderRadius: 7, borderWidth: 1.2, borderColor: colors.active, opacity: 0.6, alignItems: "center", justifyContent: "center" }}>
                  <View style={{ width: 7, height: 7, borderRadius: 4, borderWidth: 1, borderColor: colors.active, opacity: 0.5 }} />
                </View>
              ),
              text: "inner ring — its parts live under another parent here; tap to dive in",
            },
          ].map((row, i) => (
            <View key={i} style={{ flexDirection: "row", alignItems: "center", marginBottom: 7 }}>
              {row.swatch}
              <Text style={{ flex: 1, marginLeft: 8, fontSize: 11.5, color: colors.foreground }}>{row.text}</Text>
            </View>
          ))}
          <Text style={{ fontSize: 11.5, color: colors.inactive, lineHeight: 16 }}>
            size = how much life is logged in it (for the selected years) · names and icons
            appear as you zoom · click to dive in, esc or background to climb out
          </Text>
        </View>
      ) : null}

      {focus ? (
        <View
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            backgroundColor: colors.fill,
            borderColor: colors.trim,
            borderWidth: 1,
            borderRadius: 12,
            padding: 12,
            maxWidth: Math.min(340, W - 20),
          }}
        >
          {crumbs.length > 1 ? (
            <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 4 }}>
              {crumbs.slice(0, -1).map((n) => (
                <TouchableOpacity key={n.v.id} hitSlop={{ top: 4, bottom: 4 }} onPress={() => zoomToNode(n)}>
                  <Text style={{ fontSize: 11, color: colors.inactive, textDecorationLine: "underline" }}>
                    {privacy ? "•••" : (n.v.title || "").slice(0, 14)}
                    {" › "}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          ) : null}
          {privacy ? (
            <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 4 }}>
              <Icon name={focus.v.icon || focus.v.inherited_icon} color={valueColor(focus.v)} size={18} />
              <Text style={{ flex: 1, marginLeft: 8, fontSize: 14, fontWeight: "600", color: colors.foreground }} numberOfLines={2}>
                •••
              </Text>
              {!!(focus.v.parent_ids || []).length && parentsOfId !== focus.v.id ? (
                <TouchableOpacity
                  hitSlop={{ top: 8, bottom: 8 }}
                  onPress={() => enterParentsView(focus.v.id)}
                  style={{ marginRight: 10 }}
                >
                  <Text style={{ color: colors.active, fontSize: 12 }}>Parents</Text>
                </TouchableOpacity>
              ) : null}
              <TouchableOpacity hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} onPress={upOneLevel}>
                <Text style={{ color: colors.inactive, fontSize: 14 }}>⤴</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={{ flexDirection: "row", alignItems: "flex-start", marginBottom: 4 }}>
              <ScrollView
                style={{ flex: 1, maxHeight: Math.min(320, H * 0.45) }}
                nestedScrollEnabled
              >
                <MomentBullet
                  key={focus.v.id}
                  moment={{ id: `atlas-${focus.v.id}`, value: focus.v }}
                  onRefreshChapter={() => valueActions.indexLite()}
                  onRefreshMoment={() => valueActions.indexLite()}
                />
              </ScrollView>
              {!!(focus.v.parent_ids || []).length && parentsOfId !== focus.v.id ? (
                <TouchableOpacity
                  hitSlop={{ top: 8, bottom: 8 }}
                  onPress={() => enterParentsView(focus.v.id)}
                  style={{ marginRight: 10, marginTop: 2 }}
                >
                  <Text style={{ color: colors.active, fontSize: 12 }}>Parents</Text>
                </TouchableOpacity>
              ) : null}
              <TouchableOpacity hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} onPress={upOneLevel}>
                <Text style={{ color: colors.inactive, fontSize: 14 }}>⤴</Text>
              </TouchableOpacity>
            </View>
          )}
          <Text style={{ fontSize: 12, color: colors.inactive, marginBottom: 4 }}>
            {derived.own.get(focus.v.id) || 0} moments · {focusSubtree} in subtree ·{" "}
            {focus.children.length}
            {focus.totalKids > focus.children.length ? ` of ${focus.totalKids}` : ""} parts shown
          </Text>
          <TouchableOpacity onPress={() => openValue(focus.v.id)}>
            <Text style={{ fontSize: 13, color: colors.active, textDecorationLine: "underline" }}>
              open value →
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );
}

/* ═══ 1. CONSTELLATION ═══ */
function Constellation({ derived, topRoots, W, H, colors, privacy, recentGlow, openValue, valueColor }) {
  const { nodes, edges, labels, nodeById, adj } = useMemo(() => {
    const { childrenOf, subtree } = derived;
    const cx = W / 2;
    const cy = H / 2;
    const nodes = [];
    const edges = [];
    const totalW = topRoots.reduce((s, r) => s + Math.max(subtree.get(r.id) || 0, 1), 0);
    const KIDS_AT_DEPTH = [8, 6, 5, 4, 3];
    let angle = -Math.PI / 2;

    const placedPos = new Map();
    const place = (v, x, y, aCenter, span, depth, parentPos, parentId) => {
      // DAG guard: a multi-parent value is placed once; later parents
      // just get an edge drawn to the existing star
      if (placedPos.has(v.id)) {
        if (parentPos) {
          const p = placedPos.get(v.id);
          edges.push({ x1: parentPos.x, y1: parentPos.y, x2: p.x, y2: p.y, a: parentId, b: v.id });
        }
        return;
      }
      placedPos.set(v.id, { x, y });
      const w = subtree.get(v.id) || 0;
      nodes.push({ v, x, y, w, depth });
      if (parentPos) edges.push({ x1: parentPos.x, y1: parentPos.y, x2: x, y2: y, a: parentId, b: v.id });
      if (depth >= KIDS_AT_DEPTH.length) return;
      const kids = (childrenOf.get(v.id) || [])
        .filter((k) => !HIDDEN(k))
        .map((k) => ({ k, w: subtree.get(k.id) || 0 }))
        .sort((a, b) => b.w - a.w)
        .slice(0, KIDS_AT_DEPTH[depth])
        .map((e) => e.k);
      const step = span / Math.max(kids.length, 1);
      kids.forEach((k, i) => {
        const a = aCenter - span / 2 + step * (i + 0.5) + (jitter(k.id, 1) - 0.5) * step * 0.5;
        const dist = Math.min(W, H) * 0.13 * Math.pow(0.75, depth) * (0.75 + 0.5 * jitter(k.id, 2));
        const nx = Math.min(Math.max(x + dist * Math.cos(a), 14), W - 14);
        const ny = Math.min(Math.max(y + dist * Math.sin(a), 14), H - 14);
        place(k, nx, ny, a, step * 1.5, depth + 1, { x, y }, v.id);
      });
    };

    topRoots.forEach((r) => {
      const w = Math.max(derived.subtree.get(r.id) || 0, 1);
      const span = 2 * Math.PI * (w / totalW);
      const mid = angle + span / 2;
      const R = Math.min(W, H) * 0.27;
      place(r, cx + R * Math.cos(mid), cy + R * Math.sin(mid), mid, Math.max(span, 0.5), 0, null, null);
      angle += span;
    });

    const maxW = Math.max(...nodes.map((n) => n.w), 1);
    for (const n of nodes) n.r = 1.6 + 11 * Math.sqrt(n.w / maxW);
    const labels = nodes
      .slice()
      .sort((a, b) => b.w - a.w)
      .slice(0, 14);
    const nodeById = new Map(nodes.map((n) => [n.v.id, n]));
    const adj = new Map();
    for (const e of edges) {
      if (e.a == null || e.b == null) continue;
      if (!adj.has(e.a)) adj.set(e.a, new Set());
      if (!adj.has(e.b)) adj.set(e.b, new Set());
      adj.get(e.a).add(e.b);
      adj.get(e.b).add(e.a);
    }
    return { nodes, edges, labels, nodeById, adj };
  }, [derived, topRoots, W, H]);

  const [view, setView] = useState({ tx: 0, ty: 0, s: 1 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const [hoverId, setHoverId] = useState(null);
  const [sel, setSel] = useState(null);
  const containerRef = useRef(null);

  // pan / zoom / hover / click-select — web pointer handling
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const el = containerRef.current;
    if (!el || !el.addEventListener) return;
    let drag = null;
    const pt = (e) => {
      const r = el.getBoundingClientRect();
      return { px: e.clientX - r.left, py: e.clientY - r.top };
    };
    const nearest = (px, py) => {
      const v = viewRef.current;
      const x = (px - v.tx) / v.s;
      const y = (py - v.ty) / v.s;
      let best = null;
      let bd = 14 / v.s + 4;
      for (const n of nodesRef.current) {
        const d = Math.hypot(n.x - x, n.y - y) - n.r;
        if (d < bd) {
          bd = d;
          best = n;
        }
      }
      return best;
    };
    const onWheel = (e) => {
      e.preventDefault();
      const { px, py } = pt(e);
      const v = viewRef.current;
      const s2 = Math.min(Math.max(v.s * Math.exp(-e.deltaY * 0.0012), 0.5), 10);
      const wx = (px - v.tx) / v.s;
      const wy = (py - v.ty) / v.s;
      setView({ s: s2, tx: px - wx * s2, ty: py - wy * s2 });
    };
    const onDown = (e) => {
      const { px, py } = pt(e);
      drag = { px, py, tx: viewRef.current.tx, ty: viewRef.current.ty, moved: false };
    };
    const onMove = (e) => {
      const { px, py } = pt(e);
      if (drag) {
        const dx = px - drag.px;
        const dy = py - drag.py;
        if (Math.abs(dx) + Math.abs(dy) > 3) drag.moved = true;
        if (drag.moved) {
          // capture now — the updater runs later, when drag may already be null
          const ntx = drag.tx + dx;
          const nty = drag.ty + dy;
          setView((v) => ({ ...v, tx: ntx, ty: nty }));
        }
      } else {
        const n = nearest(px, py);
        setHoverId(n ? n.v.id : null);
      }
    };
    const onUp = (e) => {
      if (drag && !drag.moved) {
        const { px, py } = pt(e);
        setSel(nearest(px, py) || null);
      }
      drag = null;
    };
    const onLeave = () => {
      setHoverId(null);
      drag = null;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  const activeId = hoverId || (sel && sel.v.id) || null;
  const neighbors = activeId ? adj.get(activeId) || new Set() : null;
  const dim = !!activeId;
  const shownLabels =
    view.s > 2
      ? nodes
          .slice()
          .sort((a, b) => b.w - a.w)
          .slice(0, 40)
      : labels;
  const hoverNode = activeId ? nodeById.get(activeId) : null;

  return (
    <View style={{ flex: 1 }}>
      {/* pointer listeners on canvas only — see Pack */}
      <View
        ref={containerRef}
        style={{ flex: 1, cursor: Platform.OS === "web" ? "grab" : undefined }}
      >
      <Svg width={W} height={H}>
        <G transform={`translate(${view.tx},${view.ty}) scale(${view.s})`}>
          {edges.map((e, i) => {
            const hot = activeId && (e.a === activeId || e.b === activeId);
            return (
              <Line
                key={i}
                x1={e.x1}
                y1={e.y1}
                x2={e.x2}
                y2={e.y2}
                stroke={hot ? colors.activeHighlight : colors.inactive}
                strokeOpacity={hot ? 0.55 : dim ? 0.05 : 0.14}
                strokeWidth={(hot ? 1.4 : 0.7) / view.s}
              />
            );
          })}
          {nodes.map((n) => {
            const c = valueColor(n.v);
            const isActive = activeId === n.v.id;
            const isNb = neighbors && neighbors.has(n.v.id);
            return (
              <React.Fragment key={n.v.id}>
                {recentGlow(n.v.id) ? (
                  <Circle
                    cx={n.x}
                    cy={n.y}
                    r={n.r + 5}
                    fill={c}
                    opacity={dim && !isActive && !isNb ? 0.04 : 0.16}
                  />
                ) : null}
                <Circle
                  cx={n.x}
                  cy={n.y}
                  r={isActive ? n.r * 1.3 : n.r}
                  fill={c}
                  opacity={dim ? (isActive ? 1 : isNb ? 0.9 : 0.12) : n.v.completed_at ? 0.35 : 0.9}
                  stroke={isActive ? colors.activeHighlight : undefined}
                  strokeWidth={isActive ? 1.5 / view.s : 0}
                />
              </React.Fragment>
            );
          })}
          {!privacy
            ? shownLabels.map((n) => (
                <SvgText
                  fontFamily={FONT}
                  key={`l${n.v.id}`}
                  x={n.x + n.r + 4}
                  y={n.y + 3}
                  fontSize={10}
                  fill={colors.inactive}
                  opacity={dim && activeId !== n.v.id && !(neighbors && neighbors.has(n.v.id)) ? 0.25 : 1}
                >
                  {(n.v.title || "").slice(0, 22)}
                </SvgText>
              ))
            : null}
          {!privacy && hoverNode && !shownLabels.some((n) => n.v.id === hoverNode.v.id) ? (
            <SvgText
              fontFamily={FONT}
              x={hoverNode.x + hoverNode.r + 4}
              y={hoverNode.y + 3}
              fontSize={10}
              fill={colors.foreground}
            >
              {(hoverNode.v.title || "").slice(0, 26)}
            </SvgText>
          ) : null}
        </G>
      </Svg>
      </View>

      {sel ? (
        <View
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            backgroundColor: colors.fill,
            borderColor: colors.trim,
            borderWidth: 1,
            borderRadius: 12,
            padding: 12,
            maxWidth: 250,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 4 }}>
            <Icon name={sel.v.icon || sel.v.inherited_icon} color={valueColor(sel.v)} size={18} />
            <Text style={{ flex: 1, marginLeft: 8, fontSize: 14, fontWeight: "600", color: colors.foreground }} numberOfLines={2}>
              {privacy ? "•••" : sel.v.title}
            </Text>
            <TouchableOpacity hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} onPress={() => setSel(null)}>
              <Text style={{ color: colors.inactive, fontSize: 15 }}>×</Text>
            </TouchableOpacity>
          </View>
          <Text style={{ fontSize: 12, color: colors.inactive }}>
            {derived.own.get(sel.v.id) || 0} moments · {sel.w} in subtree
          </Text>
          <Text style={{ fontSize: 12, color: colors.inactive, marginBottom: 4 }}>
            last alive {derived.lastMonth.get(sel.v.id) || "—"} · {(sel.v.parent_ids || []).length}↑{" "}
            {((derived.childrenOf.get(sel.v.id) || []).length)}↓
          </Text>
          <TouchableOpacity onPress={() => openValue(sel.v.id)}>
            <Text style={{ fontSize: 13, color: colors.active, textDecorationLine: "underline" }}>
              open value →
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );
}

/* ═══ 2. RIVER OF YEARS ═══ */
function River({ derived, W, H, colors, privacy, stats: allStats, yearCut, valueColor }) {
  const plot = useMemo(() => {
    // river semantics: a year chip shows just that year's currents
    const stats =
      yearCut && allStats ? allStats.filter((s) => s.month.startsWith(yearCut)) : allStats;
    if (!stats || !stats.length) return null;
    const { findRoot, byId } = derived;
    // fill month range
    const first = stats.reduce((m, s) => (s.month < m ? s.month : m), "9999");
    const last = stats.reduce((m, s) => (s.month > m ? s.month : m), "0000");
    const months = [];
    let [y, m] = first.split("-").map(Number);
    while (`${y}-${String(m).padStart(2, "0")}` <= last) {
      months.push(`${y}-${String(m).padStart(2, "0")}`);
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }
    const mi = new Map(months.map((mm, i) => [mm, i]));
    // area totals
    const areaTotals = new Map();
    const areaMonthly = new Map();
    for (const s of stats) {
      const root = findRoot(s.value_id, new Set());
      const rv = byId.get(root);
      if (!rv || HIDDEN(rv)) continue;
      areaTotals.set(root, (areaTotals.get(root) || 0) + s.count);
      if (!areaMonthly.has(root)) areaMonthly.set(root, new Array(months.length).fill(0));
      areaMonthly.get(root)[mi.get(s.month)] += s.count;
    }
    const areas = Array.from(areaTotals.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 7)
      .map(([id]) => id);
    const other = new Array(months.length).fill(0);
    for (const [root, series] of areaMonthly.entries()) {
      if (!areas.includes(root)) for (let i = 0; i < months.length; i++) other[i] += series[i];
    }
    // smooth
    const smooth = (arr) =>
      arr.map((_, i) => {
        let s = 0;
        let n = 0;
        for (let d = -1; d <= 1; d++) {
          if (arr[i + d] != null) {
            s += arr[i + d];
            n += 1;
          }
        }
        return s / n;
      });
    const layers = areas.map((id) => ({ id, v: byId.get(id), series: smooth(areaMonthly.get(id)) }));
    if (other.some((x) => x > 0)) layers.push({ id: -1, v: null, series: smooth(other) });
    const totals = months.map((_, i) => layers.reduce((s, l) => s + l.series[i], 0));
    const maxT = Math.max(...totals, 1);
    return { months, layers, totals, maxT };
  }, [derived, allStats, yearCut]);

  if (!plot) {
    return (
      <Text style={{ textAlign: "center", marginTop: 40, color: colors.inactive, fontStyle: "italic" }}>
        the river needs activity stats
      </Text>
    );
  }

  const padX = 16;
  const padY = 40;
  const plotW = W - padX * 2;
  const plotH = H - padY * 2;
  const X = (i) => padX + (plotW * i) / Math.max(plot.months.length - 1, 1);
  const scale = (plotH * 0.85) / plot.maxT;

  // silhouette stacking
  const paths = [];
  const cumBelow = new Array(plot.months.length).fill(0);
  for (const layer of plot.layers) {
    const top = [];
    const bot = [];
    for (let i = 0; i < plot.months.length; i++) {
      const base = padY + plotH / 2 - (plot.totals[i] * scale) / 2;
      bot.push([X(i), base + cumBelow[i] * scale]);
      top.push([X(i), base + (cumBelow[i] + layer.series[i]) * scale]);
      cumBelow[i] += layer.series[i];
    }
    const d =
      "M " +
      bot.map(([x, yy]) => `${x.toFixed(1)} ${yy.toFixed(1)}`).join(" L ") +
      " L " +
      top
        .slice()
        .reverse()
        .map(([x, yy]) => `${x.toFixed(1)} ${yy.toFixed(1)}`)
        .join(" L ") +
      " Z";
    // label at the layer's widest month
    let bi = 0;
    layer.series.forEach((v, i) => {
      if (v > layer.series[bi]) bi = i;
    });
    paths.push({ layer, d, labelX: X(bi), labelY: (bot[bi][1] + top[bi][1]) / 2 });
  }

  const yearTicks = plot.months
    .map((mm, i) => ({ mm, i }))
    .filter(({ mm }) => mm.endsWith("-01"));

  return (
    <Svg width={W} height={H}>
      {yearTicks.map(({ mm, i }) => (
        <React.Fragment key={mm}>
          <Line x1={X(i)} y1={padY - 6} x2={X(i)} y2={H - padY + 6} stroke={colors.trim} strokeWidth={0.7} />
          <SvgText fontFamily={FONT} x={X(i) + 3} y={padY - 10} fontSize={10} fill={colors.inactive}>
            {mm.slice(0, 4)}
          </SvgText>
        </React.Fragment>
      ))}
      {paths.map(({ layer, d }) => (
        <Path
          key={layer.id}
          d={d}
          fill={layer.v ? valueColor(layer.v) : colors.inactive}
          opacity={0.75}
          stroke={colors.empty}
          strokeWidth={0.8}
        />
      ))}
      {!privacy
        ? paths.map(({ layer, labelX, labelY }) => (
            <SvgText fontFamily={FONT} key={`l${layer.id}`} x={labelX} y={labelY + 3} fontSize={10.5} fill={colors.empty} fontWeight="600" textAnchor="middle">
              {layer.v ? (layer.v.title || "").slice(0, 18) : "everything else"}
            </SvgText>
          ))
        : null}
    </Svg>
  );
}

/* ═══ 4. COMPASS ═══ */
// Direction is declared, magnitude is measured: the headings are your
// focus values (manual); needle length is recent activity (this month +
// last, from map_stats). Dim spokes = declared but drifting. Faint dots
// outside the dial = active values NOT on the compass — tap to promote.
function Compass({ derived, stats, W, H, colors, privacy, valueColor, openValue, focusIds }) {
  const [sel, setSel] = useState(null); // selected heading (spoke or tick)
  // the card rotates so the selected heading sits at north; the needle
  // floats like a real compass — damped oscillation on load and on spin
  const [rot, setRot] = useState(0);
  const [needle, setNeedle] = useState(0);
  // physical device heading (🧭 toggle): the whole instrument stays
  // world-fixed while you turn the phone
  const [headingOn, setHeadingOn] = useState(false);
  const [deviceRot, setDeviceRot] = useState(0);
  const rotRef = useRef(0);
  rotRef.current = rot;
  const rotAnimRef = useRef(null);
  const needleAnimRef = useRef(null);

  const angDelta = (target, from) => {
    let d = target - from;
    while (d > Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    return d;
  };

  useEffect(() => {
    if (!headingOn) {
      setDeviceRot(0);
      return;
    }
    let sub = null;
    let cleanupWeb = null;
    if (IS_WEB) {
      const onOri = (e) => {
        // iOS exposes compass heading (deg, CW); others give alpha (deg, CCW)
        const target =
          e.webkitCompassHeading != null
            ? (-e.webkitCompassHeading * Math.PI) / 180
            : e.alpha != null
            ? (e.alpha * Math.PI) / 180
            : null;
        if (target == null) return;
        setDeviceRot((prev) => prev + angDelta(target, prev) * 0.15); // low-pass
      };
      window.addEventListener("deviceorientation", onOri, true);
      cleanupWeb = () => window.removeEventListener("deviceorientation", onOri, true);
    } else {
      try {
        const { DeviceMotion } = require("expo-sensors");
        DeviceMotion.setUpdateInterval(50);
        sub = DeviceMotion.addListener((m) => {
          const alpha = m && m.rotation ? m.rotation.alpha : null; // radians, CCW
          if (alpha == null) return;
          setDeviceRot((prev) => prev + angDelta(alpha, prev) * 0.2);
        });
      } catch (e) {
        // sensor module not in this binary — feature silently unavailable
      }
    }
    return () => {
      if (sub) sub.remove();
      if (cleanupWeb) cleanupWeb();
    };
  }, [headingOn]);

  const toggleHeading = async () => {
    if (!headingOn && IS_WEB && typeof DeviceOrientationEvent !== "undefined" && DeviceOrientationEvent.requestPermission) {
      try {
        const res = await DeviceOrientationEvent.requestPermission();
        if (res !== "granted") return;
      } catch (e) {
        return;
      }
    }
    setHeadingOn((h) => !h);
  };

  const kickNeedle = (amp = 20) => {
    const t0 = Date.now();
    const A = amp * (0.7 + Math.random() * 0.6);
    const phase = Math.random() * Math.PI;
    if (needleAnimRef.current) cancelAnimationFrame(needleAnimRef.current);
    const step = () => {
      const t = (Date.now() - t0) / 1000;
      const a = A * Math.exp(-t * 1.4) * Math.sin(5.2 * t + phase);
      setNeedle(a);
      if (t < 3.2) needleAnimRef.current = requestAnimationFrame(step);
      else setNeedle(0);
    };
    needleAnimRef.current = requestAnimationFrame(step);
  };

  const rotateTo = (target) => {
    let from = rotRef.current;
    let delta = target - from;
    while (delta > Math.PI) delta -= 2 * Math.PI;
    while (delta < -Math.PI) delta += 2 * Math.PI;
    const t0 = Date.now();
    const dur = 750;
    if (rotAnimRef.current) cancelAnimationFrame(rotAnimRef.current);
    const step = () => {
      const t = Math.min((Date.now() - t0) / dur, 1);
      const e = 1 - Math.pow(1 - t, 3);
      setRot(from + delta * e);
      if (t < 1) rotAnimRef.current = requestAnimationFrame(step);
    };
    rotAnimRef.current = requestAnimationFrame(step);
    kickNeedle(13);
  };

  const select = (v, baseAngle) => {
    if (sel && sel.id === v.id) {
      setSel(null);
      return;
    }
    setSel(v);
    rotateTo(-Math.PI / 2 - baseAngle);
  };

  useEffect(() => {
    kickNeedle(45); // wake-up float, settling at north
    return () => {
      if (needleAnimRef.current) cancelAnimationFrame(needleAnimRef.current);
      if (rotAnimRef.current) cancelAnimationFrame(rotAnimRef.current);
    };
  }, []);

  const dial = useMemo(() => {
    const { byId } = derived;
    const now = new Date();
    const mKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const windowMonths = new Set([mKey(now), mKey(prev)]);
    const recent = new Map();
    for (const s of stats || []) {
      if (windowMonths.has(s.month))
        recent.set(s.value_id, (recent.get(s.value_id) || 0) + s.count);
    }
    const headings = (focusIds || [])
      .map((id) => byId.get(id))
      .filter((v) => v && !HIDDEN(v));
    // only ALIVE headings get spokes + labels — a big focus list would
    // otherwise drown the dial in overlapping "drifting" labels
    const active = headings
      .filter((v) => (recent.get(v.id) || 0) > 0)
      .sort((a, b) => (recent.get(b.id) || 0) - (recent.get(a.id) || 0));
    const drifting = headings.filter((v) => !(recent.get(v.id) > 0));
    const maxC = Math.max(...active.map((v) => recent.get(v.id) || 0), 1);
    const cx = W / 2;
    const cy = H / 2;
    const R = Math.min(W, H) * 0.32;
    // ONE rule decides every bearing: vitality rank. Active headings by
    // recent count, then drifting ones by how recently they last lived —
    // alternating right/left down both flanks, so north = most alive and
    // due south = longest dormant. Base angles only; the card rotation
    // applies at render.
    const lastMonth = derived.lastMonth;
    const driftingSorted = drifting
      .slice()
      .sort((a, b) => (lastMonth.get(b.id) || "").localeCompare(lastMonth.get(a.id) || ""));
    const N = Math.max(active.length + driftingSorted.length, 1);
    const angleFor = (i) => {
      const k = Math.ceil(i / 2);
      const sign = i % 2 === 1 ? 1 : -1;
      return -Math.PI / 2 + (i === 0 ? 0 : sign * k * ((2 * Math.PI) / N));
    };
    const nowKey = mKey(now);
    const monthsAgo = (id) => {
      const lm = lastMonth.get(id);
      if (!lm) return 99;
      const [y1, m1] = nowKey.split("-").map(Number);
      const [y2, m2] = lm.split("-").map(Number);
      return (y1 - y2) * 12 + (m1 - m2);
    };
    const spokes = active.map((v, i) => {
      const a = angleFor(i);
      const c = recent.get(v.id) || 0;
      const len = R * (0.3 + 0.7 * Math.sqrt(c / maxC));
      return { v, c, a, len };
    });
    // drifting headings: rim ticks fading with staleness
    const ticks = driftingSorted.map((v, i) => ({
      v,
      a: angleFor(active.length + i),
      fade: Math.max(0.12, 0.5 - monthsAgo(v.id) * 0.035),
    }));
    // uncharted pulls: active values not on the compass (chips, not dots)
    const focusSet = new Set(focusIds || []);
    const ghosts = Array.from(recent.entries())
      .filter(([id, c]) => c > 0 && !focusSet.has(id))
      .map(([id, c]) => ({ v: byId.get(id), c }))
      .filter((g) => g.v && !HIDDEN(g.v) && !g.v.completed_at)
      .sort((a, b) => b.c - a.c)
      .slice(0, 6);
    return { spokes, ticks, ghosts, cx, cy, R };
  }, [derived, stats, W, H, focusIds]);

  if (!dial.spokes.length && !dial.ticks.length) {
    return (
      <Text style={{ textAlign: "center", marginTop: 40, color: colors.inactive, fontStyle: "italic" }}>
        the compass needs headings — set some focus values first
      </Text>
    );
  }

  // display position: base angle + card rotation + physical device heading
  const dispRot = rot + deviceRot;
  const P = (a, radius) => ({
    x: dial.cx + Math.cos(a + dispRot) * radius,
    y: dial.cy + Math.sin(a + dispRot) * radius,
  });
  const needleLen = dial.R * 0.5;

  return (
    <View style={{ flex: 1 }}>
      <Svg width={W} height={H}>
        {/* housing: double ring + rose ticks that rotate with the card */}
        <Circle cx={dial.cx} cy={dial.cy} r={dial.R + 10} fill="none" stroke={colors.trim} strokeWidth={0.8} strokeOpacity={0.7} />
        <Circle cx={dial.cx} cy={dial.cy} r={dial.R} fill={colors.fill} fillOpacity={0.25} stroke={colors.trim} strokeWidth={1} />
        {Array.from({ length: 24 }).map((_, i) => {
          const a = (Math.PI * 2 * i) / 24 + dispRot;
          const major = i % 6 === 0;
          const x1 = dial.cx + Math.cos(a) * (dial.R - (major ? 12 : 6));
          const y1 = dial.cy + Math.sin(a) * (dial.R - (major ? 12 : 6));
          const x2 = dial.cx + Math.cos(a) * (dial.R - 2);
          const y2 = dial.cy + Math.sin(a) * (dial.R - 2);
          return (
            <Line key={`rose${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={colors.trim} strokeWidth={major ? 1.4 : 0.8} strokeOpacity={major ? 0.8 : 0.5} />
          );
        })}
        {/* lubber line: fixed on the housing, marks north */}
        <Path
          d={`M ${dial.cx - 6} ${dial.cy - dial.R - 18} L ${dial.cx + 6} ${dial.cy - dial.R - 18} L ${dial.cx} ${dial.cy - dial.R - 6} Z`}
          fill={colors.foreground}
          opacity={0.75}
        />
        {dial.ticks.map((t) => {
          const p1 = P(t.a, dial.R - 5);
          const p2 = P(t.a, dial.R + 5);
          const hot = sel && sel.id === t.v.id;
          return (
            <Line
              key={`tk${t.v.id}`}
              x1={p1.x}
              y1={p1.y}
              x2={p2.x}
              y2={p2.y}
              stroke={hot ? colors.activeHighlight : valueColor(t.v)}
              strokeOpacity={hot ? 0.9 : t.fade}
              strokeWidth={hot ? 2.5 : 1.5}
            />
          );
        })}
        {dial.spokes.map((sp) => {
          const c = valueColor(sp.v);
          const tip = P(sp.a, sp.len);
          const hot = sel && sel.id === sp.v.id;
          return (
            <React.Fragment key={sp.v.id}>
              <Line
                x1={dial.cx}
                y1={dial.cy}
                x2={tip.x}
                y2={tip.y}
                stroke={c}
                strokeWidth={hot ? 3.5 : 2.5}
                strokeOpacity={0.85}
                strokeLinecap="round"
              />
              <Circle cx={tip.x} cy={tip.y} r={hot ? 6.5 : 5} fill={c} fillOpacity={0.9} />
            </React.Fragment>
          );
        })}
        {/* rim labels: every drifting notch shows its name, radially */}
        {dial.ticks.map((t) => {
          const aDeg = ((t.a + dispRot) * 180) / Math.PI;
          const flip = Math.cos(t.a + dispRot) < 0;
          const p = P(t.a, dial.R + 26);
          return (
            <SvgText
              key={`tl${t.v.id}`}
              x={p.x}
              y={p.y + 3}
              fontSize={9.5}
              fill={valueColor(t.v)}
              opacity={sel && sel.id === t.v.id ? 1 : Math.min(0.85, 0.35 + t.fade)}
              fontFamily={FONT}
              textAnchor={flip ? "end" : "start"}
              transform={`rotate(${flip ? aDeg + 180 : aDeg}, ${p.x}, ${p.y})`}
            >
              {privacy ? "•••" : (t.v.title || "").slice(0, 14)}
            </SvgText>
          );
        })}
        {/* the needle: floats, settles pointing at the STRONGEST heading */}
        <G
          transform={`translate(${dial.cx},${dial.cy}) rotate(${
            (dial.spokes.length ? ((dial.spokes[0].a + dispRot) * 180) / Math.PI + 90 : 0) + needle
          })`}
        >
          <Path d={`M 0 ${-needleLen} L 7 0 L 0 10 Z`} fill={colors.activeHighlight || colors.active} opacity={0.85} />
          <Path d={`M 0 ${-needleLen} L -7 0 L 0 10 Z`} fill={colors.active} opacity={0.5} />
          <Path d={`M 0 10 L 7 0 L 0 ${needleLen * 0.45} L -7 0 Z`} fill={colors.inactive} opacity={0.35} />
        </G>
        <Circle cx={dial.cx} cy={dial.cy} r={4.5} fill={colors.foreground} />
      </Svg>
      {/* touch targets + labels float above the SVG */}
      {dial.spokes.map((sp) => {
        const lp = P(sp.a, dial.R + 30);
        const c = valueColor(sp.v);
        return (
          <TouchableOpacity
            key={`l${sp.v.id}`}
            onPress={() => select(sp.v, sp.a)}
            style={{
              position: "absolute",
              left: lp.x - 60,
              top: lp.y - 16,
              width: 120,
              alignItems: "center",
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
              <Icon name={sp.v.icon || sp.v.inherited_icon} color={c} size={13} />
              <Text numberOfLines={1} style={{ fontSize: 11.5, color: c, fontWeight: "600", maxWidth: 96 }}>
                {privacy ? "•••" : (sp.v.title || "").slice(0, 18)}
              </Text>
            </View>
            <Text style={{ fontSize: 10, color: colors.inactive }}>{sp.c} lately</Text>
          </TouchableOpacity>
        );
      })}
      {/* rim icons + touch targets — each notch is a full identity */}
      {dial.ticks.map((t) => {
        const ip = P(t.a, dial.R + 14);
        return (
          <View
            key={`ti${t.v.id}`}
            pointerEvents="none"
            style={{ position: "absolute", left: ip.x - 6, top: ip.y - 6 }}
          >
            <Icon name={t.v.icon || t.v.inherited_icon} color={valueColor(t.v)} size={11} />
          </View>
        );
      })}
      {dial.ticks.map((t) => {
        const tp = P(t.a, dial.R + 12);
        return (
          <TouchableOpacity
            key={`tt${t.v.id}`}
            onPress={() => select(t.v, t.a)}
            hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
            style={{ position: "absolute", left: tp.x - 12, top: tp.y - 12, width: 24, height: 24 }}
          />
        );
      })}
      {/* physical heading toggle: instrument follows the phone's rotation */}
      <TouchableOpacity
        onPress={toggleHeading}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        style={{ position: "absolute", top: 8, right: 14, opacity: headingOn ? 1 : 0.4 }}
      >
        <Text style={{ fontSize: 17 }}>🧭</Text>
      </TouchableOpacity>
      {/* uncharted pulls: active values not yet on the compass */}
      {dial.ghosts.length ? (
        <View style={{ position: "absolute", top: 8, left: 12, maxWidth: 190 }}>
          <Text style={{ fontSize: 10.5, color: colors.inactive, fontStyle: "italic", marginBottom: 4 }}>
            uncharted pulls
          </Text>
          {dial.ghosts.map((g) => (
            <TouchableOpacity
              key={`gc${g.v.id}`}
              onPress={() => setSel(sel && sel.id === g.v.id ? null : g.v)}
              style={{ flexDirection: "row", alignItems: "center", gap: 5, paddingVertical: 3 }}
            >
              <Icon name={g.v.icon || g.v.inherited_icon} color={valueColor(g.v)} size={12} />
              <Text numberOfLines={1} style={{ fontSize: 11.5, color: valueColor(g.v), maxWidth: 140 }}>
                {privacy ? "•••" : (g.v.title || "").slice(0, 20)}
              </Text>
              <Text style={{ fontSize: 10, color: colors.inactive }}>{g.c}</Text>
            </TouchableOpacity>
          ))}
        </View>
      ) : null}
      {/* selected heading or pull: identify it, act on it */}
      {sel ? (
        <View
          style={{
            position: "absolute",
            bottom: 34,
            alignSelf: "center",
            flexDirection: "row",
            alignItems: "center",
            backgroundColor: colors.fill,
            borderColor: colors.trim,
            borderWidth: 1,
            borderRadius: 10,
            paddingHorizontal: 12,
            paddingVertical: 7,
            gap: 12,
          }}
        >
          <Text style={{ fontSize: 12, color: colors.foreground, fontWeight: "600" }} numberOfLines={1}>
            {privacy ? "•••" : (sel.title || "").slice(0, 28)}
          </Text>
          <Text style={{ fontSize: 11, color: colors.inactive }}>
            {(() => {
              const sp = dial.spokes.find((x) => x.v.id === sel.id);
              if (sp) return `${sp.c} lately`;
              const g = dial.ghosts.find((x) => x.v.id === sel.id);
              if (g) return `${g.c} lately · uncharted`;
              return "drifting";
            })()}
          </Text>
          <TouchableOpacity onPress={() => openValue(sel.id)}>
            <Text style={{ fontSize: 12, color: colors.active, textDecorationLine: "underline" }}>open →</Text>
          </TouchableOpacity>
          {(focusIds || []).includes(sel.id) ? (
            <TouchableOpacity
              onPress={() => {
                prefActions.removeFocus(sel.id);
                setSel(null);
              }}
            >
              <Text style={{ fontSize: 12, color: colors.inactive, textDecorationLine: "underline" }}>release</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              onPress={() => {
                prefActions.addFocus(sel.id);
                setSel(null);
              }}
            >
              <Text style={{ fontSize: 12, color: colors.active, textDecorationLine: "underline" }}>add to compass</Text>
            </TouchableOpacity>
          )}
        </View>
      ) : null}
    </View>
  );
}

/* ═══ 3. FRACTAL GARDEN ═══ */
function Garden({ derived, topRoots, W, H, colors, privacy, recentGlow, valueColor }) {
  const { lines, leaves, trunkLabels } = useMemo(() => {
    const { childrenOf, subtree } = derived;
    const lines = [];
    const leaves = [];
    const trunkLabels = [];
    const roots = topRoots.slice(0, 5);
    const baseY = H - 34;
    const maxW = Math.max(...roots.map((r) => subtree.get(r.id) || 0), 1);

    const branch = (v, x, y, angleRad, len, depth) => {
      const x2 = x + len * Math.cos(angleRad);
      const y2 = y + len * Math.sin(angleRad);
      const w = subtree.get(v.id) || 0;
      lines.push({
        x1: x,
        y1: y,
        x2,
        y2,
        width: Math.min(Math.max(Math.log2(w + 2) * 1.5, 1.2), 11),
        color: valueColor(v),
        done: !!v.completed_at,
      });
      if (recentGlow(v.id)) leaves.push({ x: x2, y: y2, color: valueColor(v) });
      if (depth >= 5 || len < 8) return;
      const kids = (childrenOf.get(v.id) || [])
        .filter((k) => !HIDDEN(k))
        .map((k) => ({ k, w: subtree.get(k.id) || 0 }))
        .sort((a, b) => b.w - a.w)
        .slice(0, 5)
        .map((e) => e.k);
      const spread = (100 * Math.PI) / 180;
      const step = kids.length > 1 ? spread / (kids.length - 1) : 0;
      kids.forEach((k, i) => {
        const a = angleRad - spread / 2 + step * i + (jitter(k.id, 3) - 0.5) * 0.25;
        branch(k, x2, y2, a, len * 0.72 * (0.85 + 0.3 * jitter(k.id, 4)), depth + 1);
      });
    };

    roots.forEach((r, i) => {
      const x = (W * (i + 1)) / (roots.length + 1);
      const wNorm = (subtree.get(r.id) || 0) / maxW;
      const len = H * 0.22 * (0.6 + 0.4 * wNorm);
      branch(r, x, baseY, -Math.PI / 2, len, 0);
      trunkLabels.push({ x, y: baseY + 16, title: r.title, color: valueColor(r) });
    });
    return { lines, leaves, trunkLabels };
  }, [derived, topRoots, W, H]);

  return (
    <Svg width={W} height={H}>
      <Line x1={12} y1={H - 34} x2={W - 12} y2={H - 34} stroke={colors.trim} strokeWidth={1} />
      {lines.map((l, i) => (
        <Line
          key={i}
          x1={l.x1}
          y1={l.y1}
          x2={l.x2}
          y2={l.y2}
          stroke={l.color}
          strokeWidth={l.width}
          strokeOpacity={l.done ? 0.3 : 0.8}
          strokeLinecap="round"
        />
      ))}
      {leaves.map((lf, i) => (
        <React.Fragment key={i}>
          <Circle cx={lf.x} cy={lf.y} r={5} fill={lf.color} opacity={0.25} />
          <Circle cx={lf.x} cy={lf.y} r={2.3} fill={lf.color} opacity={0.9} />
        </React.Fragment>
      ))}
      {!privacy
        ? trunkLabels.map((t, i) => (
            <SvgText fontFamily={FONT} key={i} x={t.x} y={t.y} fontSize={10.5} fill={colors.inactive} textAnchor="middle">
              {(t.title || "").slice(0, 20)}
            </SvgText>
          ))
        : null}
    </Svg>
  );
}
